import { useEffect } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { useFormContext } from 'react-hook-form';

import styles from './AmountScreen.module.css';
import { useNumberFormat } from '../../../../hooks/useNumberFormat';
import { TMobileSignupForm } from '../../form/form';
import { useMobileSignupNavContext } from '../../hooks/useMobileSignupNavContext';
import { RangeSlider } from '../../RangeSlider/RangeSlider';
import { StepLayout } from '../../StepLayout/StepLayout';

const MIN_AMOUNT = 1000;
const MAX_AMOUNT = 500000;
const STEP = 1000;
const DEFAULT_AMOUNT = 25000;

// DIT-669. Reads/writes the shared mobile signup form store (DIT-664). The
// store keeps `amount` as a string (as the persistence layer expects); the
// slider works in numbers, so we convert only at that boundary.
export const AmountScreen = () => {
  const { t } = useLingui();
  const { formatCurrency } = useNumberFormat();
  const { watch, setValue } = useFormContext<TMobileSignupForm>();
  const { goNext } = useMobileSignupNavContext();

  const amount = watch('amount');
  const sliderValue = Number(amount) || DEFAULT_AMOUNT;

  // Seed the default so the value shown on the slider is also what gets
  // persisted. Non-dirty, so the provider's async reset from a saved
  // application still wins if the user already has an amount.
  useEffect(() => {
    if (!amount) {
      setValue('amount', String(DEFAULT_AMOUNT), { shouldDirty: false });
    }
  }, [amount, setValue]);

  const isValid = sliderValue >= MIN_AMOUNT && sliderValue <= MAX_AMOUNT;

  return (
    <StepLayout
      stageTitle={t`Credit request`}
      heading={t`How much would you like to borrow?`}
      ctaDisabled={!isValid}
      onContinue={goNext}
    >
      <p className={styles.amount}>{formatCurrency(sliderValue)}</p>
      <p className={styles.note}>
        <Trans>non-binding · doesn't affect your credit score</Trans>
      </p>
      <RangeSlider
        min={MIN_AMOUNT}
        max={MAX_AMOUNT}
        step={STEP}
        value={sliderValue}
        onChange={(value) =>
          setValue('amount', String(value), { shouldDirty: true })
        }
        ariaLabel={t`How much would you like to borrow?`}
        minLabel={formatCurrency(MIN_AMOUNT)}
        maxLabel={formatCurrency(MAX_AMOUNT)}
      />
    </StepLayout>
  );
};
